import { useState, useEffect } from 'react';
import { useScore } from '../../../context/ScoreContext';

const FONT_DECO = "'Cinzel', serif";
const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";

const SESSION_KEY = 'srs-hidden-mission-dismissed-v1';

export default function HiddenMissionOverlay() {
  const { scheduleState, setHiddenMissionActive } = useScore();
  const { hiddenMissionActive } = scheduleState;
  const [visible, setVisible] = useState(false);
  // Use sessionStorage so refresh doesn't re-show the overlay if already dismissed
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(SESSION_KEY) === '1');

  useEffect(() => {
    if (hiddenMissionActive && !dismissed) {
      setTimeout(() => setVisible(true), 80);
    } else {
      setVisible(false);
    }
  }, [hiddenMissionActive, dismissed]);

  // Reset dismissed when mission deactivated (crafter resets it)
  useEffect(() => {
    if (!hiddenMissionActive) {
      setDismissed(false);
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [hiddenMissionActive]);

  if (!hiddenMissionActive && !visible) return null;

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(() => {
      setDismissed(true);
      sessionStorage.setItem(SESSION_KEY, '1');
    }, 400);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-4"
      style={{
        background: 'rgba(2,4,18,0.92)',
        backdropFilter: 'blur(8px)',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.4s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
      onClick={handleDismiss}
    >
      {/* Particle dots */}
      {Array.from({ length: 18 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 3 + (i % 4),
            height: 3 + (i % 4),
            left: `${5 + (i * 5.2) % 90}%`,
            top: `${10 + (i * 7.3) % 80}%`,
            background: i % 3 === 0 ? '#c06b9a' : i % 3 === 1 ? '#d4a843' : '#7ec8a0',
            opacity: 0.3 + (i % 5) * 0.1,
            animation: `hmFloat ${2.5 + (i % 3) * 0.8}s ${(i * 0.2) % 2}s ease-in-out infinite alternate`,
          }}
        />
      ))}

      <div
        className="relative w-full max-w-sm rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, #0e0818, #06040f)',
          border: '1.5px solid rgba(192,107,154,0.5)',
          boxShadow: '0 0 60px rgba(192,107,154,0.3), 0 0 120px rgba(192,107,154,0.1)',
          animation: visible ? 'hmPop 0.5s cubic-bezier(0.175,0.885,0.32,1.275)' : 'none',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Top glow line */}
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(192,107,154,0.9), transparent)' }} />

        {/* Corner brackets */}
        <div className="absolute top-3 left-3 w-4 h-4 pointer-events-none" style={{ borderTop: '1.5px solid rgba(192,107,154,0.7)', borderLeft: '1.5px solid rgba(192,107,154,0.7)' }} />
        <div className="absolute top-3 right-3 w-4 h-4 pointer-events-none" style={{ borderTop: '1.5px solid rgba(192,107,154,0.7)', borderRight: '1.5px solid rgba(192,107,154,0.7)' }} />
        <div className="absolute bottom-3 left-3 w-4 h-4 pointer-events-none" style={{ borderBottom: '1.5px solid rgba(192,107,154,0.7)', borderLeft: '1.5px solid rgba(192,107,154,0.7)' }} />
        <div className="absolute bottom-3 right-3 w-4 h-4 pointer-events-none" style={{ borderBottom: '1.5px solid rgba(192,107,154,0.7)', borderRight: '1.5px solid rgba(192,107,154,0.7)' }} />

        {/* Sweep shimmer */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 bottom-0 w-1/3" style={{ background: 'linear-gradient(90deg, transparent, rgba(192,107,154,0.08), transparent)', animation: 'hmSweep 3s ease-in-out infinite', left: '-100%' }} />
        </div>

        <div className="px-6 pt-8 pb-6 text-center">
          {/* Icon */}
          <div className="relative w-20 h-20 mx-auto mb-5 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(circle, rgba(192,107,154,0.25), transparent 70%)', animation: 'hmOrb 2s ease-in-out infinite' }} />
            <div className="w-16 h-16 rounded-full flex items-center justify-center relative z-10"
              style={{ background: 'linear-gradient(135deg, rgba(192,107,154,0.25), rgba(192,107,154,0.08))', border: '1.5px solid rgba(192,107,154,0.5)' }}>
              <i className="ri-eye-close-line text-3xl" style={{ color: '#c06b9a' }} />
            </div>
            <div className="absolute inset-0 rounded-full pointer-events-none" style={{ border: '1px solid rgba(192,107,154,0.3)', animation: 'hmRing 2s ease-out infinite' }} />
          </div>

          {/* Unlock label */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="h-px w-8" style={{ background: 'rgba(192,107,154,0.4)' }} />
            <span className="text-[10px] font-black tracking-[0.4em] uppercase" style={{ color: 'rgba(192,107,154,0.7)', fontFamily: FONT_DECO }}>Hidden Mission Unlocked</span>
            <div className="h-px w-8" style={{ background: 'rgba(192,107,154,0.4)' }} />
          </div>

          {/* Title */}
          <h2 className="text-white text-xl font-black mb-1 leading-tight" style={{ fontFamily: FONT_DECO, textShadow: '0 0 20px rgba(192,107,154,0.6)' }}>
            微光記憶
          </h2>
          <p className="text-sm font-bold mb-5" style={{ color: 'rgba(192,107,154,0.8)', fontFamily: FONT_BODY, letterSpacing: '0.2em' }}>
            ✦ 民宿密令 ✦
          </p>

          {/* Divider */}
          <div className="flex items-center gap-2 mb-4">
            <div className="h-px flex-1" style={{ background: 'rgba(192,107,154,0.2)' }} />
            <i className="ri-sparkling-2-fill text-xs" style={{ color: 'rgba(192,107,154,0.5)' }} />
            <div className="h-px flex-1" style={{ background: 'rgba(192,107,154,0.2)' }} />
          </div>

          {/* Mission content */}
          <div className="rounded-xl px-4 py-4 mb-5 text-left" style={{ background: 'rgba(192,107,154,0.06)', border: '1px solid rgba(192,107,154,0.2)' }}>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(220,180,210,0.85)', fontFamily: FONT_BODY }}>
              時光檔案室發生了意外，所有的記憶被壓縮成一張張微小的碎片，散落在民宿各處。
            </p>
            <p className="text-sm leading-relaxed mt-2" style={{ color: 'rgba(220,180,210,0.85)', fontFamily: FONT_BODY }}>
              兩支隊伍必須在 <span className="font-black" style={{ color: '#c06b9a' }}>40 分鐘</span>內，儘可能蒐集所有的微光碎片，同時擅用各自的腳色能力，獲得勝利！
            </p>
          </div>

          {/* Timer badge */}
          <div className="grid grid-cols-2 gap-2 mb-6">
            <div className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(212,168,67,0.12)', border: '1px solid rgba(212,168,67,0.3)' }}>
              <i className="ri-timer-line text-sm shrink-0" style={{ color: '#d4a843' }} />
              <span className="text-sm font-black whitespace-nowrap" style={{ color: '#d4a843', fontFamily: FONT_BODY }}>限時 40 分鐘</span>
            </div>
            <div className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(126,200,160,0.12)', border: '1px solid rgba(126,200,160,0.3)' }}>
              <i className="ri-group-line text-sm shrink-0" style={{ color: '#7ec8a0' }} />
              <span className="text-sm font-black whitespace-nowrap" style={{ color: '#7ec8a0', fontFamily: FONT_BODY }}>雙隊競賽</span>
            </div>
          </div>

          {/* Confirm button */}
          <button
            onClick={handleDismiss}
            className="w-full py-3.5 rounded-xl font-black text-sm cursor-pointer transition-all active:scale-[0.97] relative overflow-hidden whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, rgba(192,107,154,0.3), rgba(192,107,154,0.15))', color: '#e8a0d0', border: '1.5px solid rgba(192,107,154,0.5)', fontFamily: FONT_BODY, boxShadow: '0 0 20px rgba(192,107,154,0.2)' }}
          >
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent, rgba(192,107,154,0.15), transparent)', animation: 'hmSweep 2s linear infinite' }} />
            <span className="relative z-10 flex items-center justify-center gap-2">
              <i className="ri-check-line" />
              收到！準備出發
            </span>
          </button>

          <p className="text-[10px] mt-3" style={{ color: 'rgba(192,107,154,0.35)', fontFamily: FONT_BODY }}>
            任務詳情已同步至冒險旅程
          </p>
        </div>
      </div>

      <style>{`
        @keyframes hmPop { from{opacity:0;transform:scale(0.75)} to{opacity:1;transform:scale(1)} }
        @keyframes hmFloat { from{transform:translateY(0)} to{transform:translateY(-12px)} }
        @keyframes hmSweep { from{left:-100%} to{left:200%} }
        @keyframes hmOrb { 0%,100%{transform:scale(1);opacity:0.8} 50%{transform:scale(1.2);opacity:0.4} }
        @keyframes hmRing { 0%{transform:scale(1);opacity:0.6} 100%{transform:scale(1.8);opacity:0} }
      `}</style>
    </div>
  );
}
